/* ╔══════════════════════════════════════════════════════════════════╗
   ║  v3 Product builder MATS — wizard engine                         ║
   ║  Runs on top of PTCProductBuilder_v2.js (unchanged). Every DOM   ║
   ║  id owned by the engine is left untouched; this file only adds   ║
   ║  step navigation, progress UI, the mobile fixed bars, gating,    ║
   ║  the order summary and the load-time auto-scroll.                ║
   ╚══════════════════════════════════════════════════════════════════╝ */
(function () {
  'use strict';

  const MOBILE = window.matchMedia('(max-width: 63.9375em)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TOP_BAR_H = 48;   // must match --v3-topbar-h in the css
  const BOTTOM_CLEAR = 84; // bottom bar height + breathing room

  const V3Wizard = {
    steps: [],
    rail: [],
    chips: [],
    current: 0,
    visited: [],
    busy: false,
    inited: false,
    autoScrolled: false,
    topShown: false,
    topBarEl: null,
    bottomBarEl: null,
    barCta: null,
    submitBtn: null,

    init: function () {
      if (this.inited) return;
      if (typeof PTCProductBuilder === 'undefined') return;
      const root = document.getElementById('productBuilder');
      const fields = document.getElementById('productBuilderFields');
      if (!root || !fields) return;
      this.inited = true;

      this.steps = Array.from(document.querySelectorAll('.v3-step'));
      if (this.steps.length < 2) return;

      this.rail = Array.from(document.querySelectorAll('#progress .steps'));
      this.chips = Array.from(document.querySelectorAll('.v3-chip'));
      this.topBarEl = document.getElementById('v3TopBar');
      this.bottomBarEl = document.getElementById('v3BottomBar');
      this.barCta = document.getElementById('v3BarNext');
      this.submitBtn = document.getElementById('submitProductBuilder');

      this.visited = this.steps.map(() => false);

      this._bind();
      this.render(0, { scroll: false });
      this._initObservers();
      this._initSwatchArrows();
      this._initAutoScroll();
      this._topbarState();
    },

    /* ── bind events ─────────────────────────────────────────────── */
    _bind: function () {
      const self = this;

      // in-card nav buttons
      document.querySelectorAll('[data-v3-next]').forEach((btn) =>
        btn.addEventListener('click', function (e) { self.goNext(e); })
      );
      document.querySelectorAll('[data-v3-prev]').forEach((btn) =>
        btn.addEventListener('click', function (e) { self.goPrev(e); })
      );

      // mobile bottom bar CTA
      if (this.barCta) {
        this.barCta.addEventListener('click', function (e) { self.attemptSubmit(e); });
      }

      // mobile progress chips: only previously visited steps are tappable
      this.chips.forEach((chip, i) => {
        chip.addEventListener('click', function () {
          if (self.visited[i]) self.goTo(i);
        });
        chip.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (self.visited[i]) self.goTo(i); }
        });
      });

      // car selects: re-render progress once step 1 becomes complete
      ['carBrand', 'carModel', 'carSubmodel', 'carFixingSystem'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.addEventListener('change', () => this.renderProgress());
      });

      // submit interception (capture phase runs before the engine's bubble
      // listener bound during PTCProductBuilder.init)
      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', function (e) { self.attemptSubmit(e); }, true);
      }

      // info popovers (delegated)
      const infoScope = document.getElementById('productBuilderFields') || document;
      infoScope.addEventListener('click', (e) => {
        const btn = e.target.closest('.v3-info');
        if (btn) { this._toggleInfo(btn); return; }
        this._closeInfo();
      });
      document.addEventListener('pointerdown', (e) => {
        if (!e.target.closest('.v3-infoWrap')) this._closeInfo();
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this._closeInfo(); });
      document.addEventListener('scroll', () => this._topbarState(), { passive: true });
      window.addEventListener('resize', () => { this._closeInfo(); this._topbarState(); });

      // flag manual scrolling so the post-transition scroll correction
      // stands down instead of fighting the user
      this._userScrolled = false;
      document.addEventListener('wheel', () => { this._userScrolled = true; }, { passive: true });
      document.addEventListener('touchstart', () => { this._userScrolled = true; }, { passive: true });
      document.addEventListener('keydown', (e) => {
        if (e.key.indexOf('Arrow') === 0 || e.key === ' ' || e.key.indexOf('Page') === 0 ||
            e.key === 'Home' || e.key === 'End') {
          this._userScrolled = true;
        }
      });
    },

    /* ── navigation ──────────────────────────────────────────────── */
    canLeave: function (from) {
      // step 0 (car) must be complete before continuing
      if (from === 0) {
        try {
          return PTCProductBuilder.validate() !== false;
        } catch (err) {
          return true;
        }
      }
      return true;
    },

    markStepError: function (i) {
      const card = this.steps[i];
      const no = card && card.querySelector('.v3-stepNo');
      if (no) { no.classList.add('err'); setTimeout(() => no.classList.remove('err'), 900); }
      if (card) {
        card.classList.add('error');
        setTimeout(() => card.classList.remove('error'), 550);
      }
    },

    goNext: function (e) {
      e.preventDefault();
      if (this.busy) return;
      if (!this.canLeave(this.current)) {
        this.markStepError(this.current);
        return;
      }
      if (this.current < this.steps.length - 1) this.goTo(this.current + 1);
    },

    goPrev: function (e) {
      if (e) e.preventDefault();
      if (this.current > 0) this.goTo(this.current - 1);
    },

    goTo: function (i, opts) {
      opts = opts || {};
      if (i < 0 || i >= this.steps.length) return;
      if (i === this.current) {
        // tapping the active chip: just make sure it is in view
        this._scrollStepIntoView(i);
        return;
      }
      const prev = this.current;
      const prevCard = this.steps[prev];

      // drop focus from whatever was tapped inside the outgoing card —
      // otherwise some browsers scroll to "keep the focused element visible"
      // right after its card becomes display:none, fighting our anchor
      if (document.activeElement && prevCard.contains(document.activeElement)) {
        document.activeElement.blur();
      }

      prevCard.classList.remove('active');
      this.current = i;
      this.visited[i] = true;
      const card = this.steps[i];
      card.classList.add('active');

      this.renderProgress();
      this._syncBar();

      if (this._isEmbroideryStep(card)) this._onEmbroideryStepShown(card);

      const self = this;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          self._scrollStepIntoView(self.current);
          self._refreshSwatches();
          setTimeout(self._refreshSwatches.bind(self), 300);
          if (self.current === self.steps.length - 1) {
            self.refreshSummary();
            setTimeout(self.refreshSummary.bind(self), 600);
          }
        });
      });
    },

    _scrollStepIntoView: function (i) {
      const card = this.steps[i];
      if (!card) return;
      const rect = card.getBoundingClientRect();

      if (MOBILE.matches) {
        // Every step change ends at the same anchor so the view never looks
        // half-cut: [fixed top bar] [gallery — always fully visible]
        // [step card starts right under it]. If the step card is taller than
        // the remaining space, part of the OPTIONS may fall below the fold —
        // that is fine and expected; the gallery itself is never cropped and
        // the fixed bottom bar always shows the action.
        const galleryCol = document.querySelector('.prodImgs');
        if (galleryCol) {
          const bottomClear = (this.bottomBarEl ? this.bottomBarEl.offsetHeight : BOTTOM_CLEAR) + 12;
          const r = galleryCol.getBoundingClientRect();
          const availH = window.innerHeight - (TOP_BAR_H + 8) - bottomClear;

          let targetY;
          if (r.height > availH) {
            // gallery taller than the visible area: keep its bottom above
            // the bottom bar (its top may slide under the slim top bar)
            targetY = r.bottom + window.scrollY - (window.innerHeight - bottomClear);
          } else {
            // normal case: gallery top sits just under the top bar
            targetY = r.top + window.scrollY - (TOP_BAR_H + 8);
          }
          targetY = Math.max(0, targetY);

          this._userScrolled = false;
          if (Math.abs(window.scrollY - targetY) > 2) {
            window.scrollTo({ top: targetY, behavior: REDUCED.matches ? 'auto' : 'smooth' });
          }

          // late corrections in case layout settled after measuring or the
          // native smooth scroll got cancelled mid-flight (iOS momentum) —
          // never override the user if they started scrolling meanwhile
          const self = this;
          const fix = function () {
            if (self._userScrolled) return;
            const r2 = galleryCol.getBoundingClientRect();
            const finalY = Math.max(0, (r2.height > availH)
              ? (r2.bottom + window.scrollY - (window.innerHeight - bottomClear))
              : (r2.top + window.scrollY - (TOP_BAR_H + 8)));
            if (Math.abs(window.scrollY - finalY) > 8) window.scrollTo(0, finalY);
          };
          setTimeout(fix, 250);
          setTimeout(fix, 600);
          return;
        }
        // fallback (no gallery on the page): keep the card top below the bar
        if (rect.top < TOP_BAR_H + 8 && rect.bottom <= window.innerHeight - BOTTOM_CLEAR) {
          window.scrollBy({ top: rect.top - (TOP_BAR_H + 8), behavior: REDUCED.matches ? 'auto' : 'smooth' });
        }
        return;
      }

      // desktop: keep the fields column (and gallery, same row start)
      // aligned near the top of the viewport
      const fieldsTop = document.getElementById('productBuilderFields').getBoundingClientRect().top;
      const y = window.scrollY;
      if (fieldsTop - 16 < 0) {
        window.scrollTo({ top: y + fieldsTop - 24, behavior: REDUCED.matches ? 'auto' : 'smooth' });
      } else if (rect.bottom > window.innerHeight - 40) {
        // step taller than the viewport and its nav row out of sight —
        // nudge down just enough to expose the bottom (gallery may slide
        // out only for genuinely tall steps)
        window.scrollBy({ top: rect.bottom - window.innerHeight + 80, behavior: REDUCED.matches ? 'auto' : 'smooth' });
      }
    },

    /* ── progress rendering (rail + chips + card badges) ─────────── */
    render: function (i, opts) {
      this.current = i;
      this.steps.forEach((s, idx) => s.classList.toggle('active', idx === i));
      this.visited[i] = true;
      this.renderProgress();
      this._syncBar();
    },

    renderProgress: function () {
      const len = this.steps.length;
      const carDone = this._carStepDone();
      if (carDone) this.visited[0] = true;

      const paint = (node, i, nrSel, txtSel) => {
        const nr = node.querySelector(nrSel);
        const done = this.visited[i] && i < this.current;
        const active = i === this.current;
        node.classList.toggle('done', done);
        node.classList.toggle('active', active);
        if (nr) nr.textContent = done ? '✓' : (i + 1);
        node.setAttribute('data-step', i);
        this.steps[i] && this.steps[i].setAttribute('data-step', i);
      };

      this.rail.forEach((node, i) => paint(node, i, '.stepNr'));
      this.chips.forEach((node, i) => {
        paint(node, i, '.v3-chipNr');
        node.classList.toggle('isNav', !!this.visited[i]);
        node.setAttribute('role', 'tab');
        node.setAttribute('aria-selected', i === this.current ? 'true' : 'false');
      });
      this.steps.forEach((card, i) => {
        const no = card.querySelector('.v3-stepNo');
        if (!no) return;
        const done = this.visited[i] && i < this.current;
        no.classList.toggle('done', done);
        no.textContent = done ? '✓' : (i + 1);
        card.setAttribute('data-step', i);
      });
    },

    _carStepDone: function () {
      // Read-only "is the car chain complete" check — must NOT use the
      // engine's validate(), because that one writes the "alege o optiune"
      // error text into the #carXError spans (which would show the errors
      // on first page load).
      try {
        const active = PTCProductBuilder.activeCarSettings || {};
        let any = false;
        for (const key in active) {
          if (!active[key]) continue;
          any = true;
          const el = document.getElementById(key);
          if (el && !el.value) return false;
        }
        return any; // no required active car setting → step counts as done
      } catch (err) {
        return false;
      }
    },

    /* ── bottom bar state (label mirrors engine submit button) ───── */
    _syncBar: function () {
      if (!this.barCta) return;
      const onFinal = this.current === this.steps.length - 1;
      if (!onFinal) {
        this.barCta.textContent = 'Continuă →';
      } else if (this.submitBtn) {
        this.barCta.textContent = this.submitBtn.textContent || 'Adauga in cos';
      }
    },

    /* ── submit interception (shared by ATC + bar CTA) ───────────── */
    attemptSubmit: function (e) {
      if (this.busy) { e.preventDefault(); e.stopImmediatePropagation(); return; }
      const onFinal = this.current === this.steps.length - 1;

      if (!onFinal) {
        // CTA / next while not on the final step → wizard navigation
        e.preventDefault();
        e.stopImmediatePropagation();
        if (!this.canLeave(this.current)) {
          this.markStepError(this.current);
          return;
        }
        this.goTo(this.current + 1);
        return;
      }

      // final step → engine submit, but gate the car chain first
      let ok = true;
      try { ok = PTCProductBuilder.validate() !== false; } catch (err) { ok = true; }
      if (!ok) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.goTo(0, { scroll: false });
        const self = this;
        setTimeout(() => { self.markStepError(0); self._scrollStepIntoView(0); }, 60);
        return;
      }

      if (this.busy) { e.preventDefault(); e.stopImmediatePropagation(); return; }

      // from the real (desktop) button: let the engine's own bubble
      // listener run submit — do nothing here. From the mobile bar:
      // invoke the engine directly.
      if (e.target === this.barCta && typeof PTCProductBuilder.submit === 'function') {
        this.busy = true;
        try { PTCProductBuilder.submit(e); } finally { /* engine re-enables the button; observer clears busy */ }
      }
    },

    /* ── live mirrors: price + submit button → bottom bar ────────── */
    _initObservers: function () {
      const self = this;
      const priceSrc = document.getElementById('price');
      const regSrc = document.getElementById('regularPrice');
      const priceDst = document.getElementById('v3BarPrice');
      const regDst = document.getElementById('v3BarRegular');

      const copyPrice = function () {
        if (!priceDst || !priceSrc) return;
        priceDst.textContent = priceSrc.textContent || '';
        if (regSrc && regDst) {
          regDst.textContent = regSrc.textContent || '';
          regDst.style.display = regSrc.style.display || (regSrc.textContent ? '' : 'none');
        }
      };

      const copyButton = function () {
        if (!self.barCta || !self.submitBtn) return;
        self.barCta.textContent = self.submitBtn.textContent || 'Adauga in cos';
        self.barCta.disabled = !!self.submitBtn.disabled;
        if (!self.submitBtn.disabled) self.busy = false;
      };

      const mo = window.MutationObserver ? new MutationObserver(copyPrice) : null;
      if (mo && priceSrc) {
        mo.observe(priceSrc, { childList: true, subtree: true, characterData: true, attributes: true });
        if (regSrc) mo.observe(regSrc, { childList: true, subtree: true, characterData: true, attributes: true });
      }
      const mb = window.MutationObserver ? new MutationObserver(copyButton) : null;
      if (mb && self.submitBtn) {
        mb.observe(self.submitBtn, { attributes: true, childList: true, subtree: true, characterData: true });
      }
      copyPrice();
      copyButton();
    },

    /* ── scroll affordance for overflowing swatch rows ───────────── */
    _initSwatchArrows: function () {
      const self = this;
      this._swatchRows = this._swatchRows || [];
      document.querySelectorAll('.option-values.v3-swatches').forEach((row) => {
        // wrapper provides the relative positioning context for the
        // fade + arrow; the row itself stays the scroll container
        const wrap = document.createElement('span');
        wrap.className = 'v3-swatchWrap';
        row.parentNode.insertBefore(wrap, row);
        wrap.appendChild(row);

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'v3-swatchArrow';
        btn.setAttribute('aria-label', 'Derulează culorile');
        btn.setAttribute('title', 'Derulează culorile');
        btn.textContent = '›';
        wrap.appendChild(btn);

        const update = function () {
          const over = row.scrollWidth - row.clientWidth > 4;
          const atEnd = !over ||
            row.scrollLeft + row.clientWidth >= row.scrollWidth - 4;
          wrap.classList.toggle('v3-scrollable', over);
          wrap.classList.toggle('v3-atEnd', atEnd);
        };

        btn.addEventListener('click', function () {
          if (REDUCED.matches) {
            row.scrollLeft += Math.min(row.clientWidth * 0.8,
              row.scrollWidth - row.scrollLeft - row.clientWidth);
          } else {
            row.scrollBy({ left: row.clientWidth * 0.8, behavior: 'smooth' });
          }
        });
        row.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        this._swatchRows.push({ row: row, wrap: wrap, update: update });
      });
      this._refreshSwatches();
    },

    /* re-measure the swatch rows — they are inside a hidden step card
       at init, so overflow can only be known once that card is visible */
    _refreshSwatches: function () {
      (this._swatchRows || []).forEach((entry) => entry.update());
    },

    /* ── order summary (final step) ──────────────────────────────── */
    refreshSummary: function () {
      const set = function (id, val) {
        const el = document.getElementById(id);
        if (el) el.textContent = val;
      };

      const selText = function (id) {
        const el = document.getElementById(id);
        if (!el) return '—';
        const opt = el.options && el.options[el.selectedIndex];
        return opt && opt.textContent ? opt.textContent.trim() : '—';
      };
      const parts = [selText('carBrand'), selText('carModel'), selText('carSubmodel')]
        .filter((t) => t && t !== '—' && t.indexOf('Selecteaza') !== 0 && t.indexOf('Selectați') !== 0 && t.indexOf('Alege') !== 0);
      set('v3-sumCar', parts.length ? parts.join(' · ') : '—');

      const valueOf = function (groupSel) {
        const el = document.querySelector(groupSel + ' .value');
        const val = el ? el.textContent.trim() : '';
        return val || '—';
      };
      set('v3-sumBase', valueOf('#baseColor'));
      set('v3-sumBorder', valueOf('#borderColor'));
      set('v3-sumThread', valueOf('#threadColor') + ' · ' + valueOf('#threadType'));

      // when the product has no embroidery step, the summary row is gone
      const hasEmbStep = this.steps.some((card) => this._isEmbroideryStep(card));
      const embRow = document.getElementById('v3-sumEmbroidery');
      if (embRow && embRow.closest) {
        const rowEl = embRow.closest('.v3-sumRow');
        if (rowEl) rowEl.style.display = hasEmbStep ? '' : 'none';
      }

      let emb = 'Nu';
      try {
        if (PTCProductBuilder.currentSettings && PTCProductBuilder.currentSettings.embroidery === 'yes') emb = 'Da';
      } catch (err) { /* noop */ }
      set('v3-sumEmbroidery', emb);

      const itemsRow = document.getElementById('v3-sumEmbroideryItemsRow');
      let count = 0;
      if (emb === 'Da') {
        try {
          const keys = PTCProductBuilder.embroideryItemKeys || [];
          keys.forEach((k) => {
            const actions = PTCProductBuilder.embroideryBuilder &&
              PTCProductBuilder.embroideryBuilder.settings &&
              PTCProductBuilder.embroideryBuilder.settings.getItemActions
              ? (PTCProductBuilder.embroideryBuilder.settings.getItemActions(k, 'page') || []) : [];
            count += actions.length;
          });
        } catch (err) { count = 0; }
      }
      if (itemsRow) itemsRow.hidden = count <= 0;
      if (count > 0) set('v3-sumEmbroideryItems', count + (count === 1 ? ' element' : ' elemente'));
    },

    /* ── embroidery step helpers ─────────────────────────────────── */
    _isEmbroideryStep: function (card) {
      return !!card && !!card.querySelector('#embroideryBuilder');
    },

    _onEmbroideryStepShown: function (card) {
      const builder = card.querySelector('#embroideryBuilder');
      const glide = card.querySelector('#embroideryImages');
      if (!builder || !glide) return;
      const self = this;
      requestAnimationFrame(function () {
        // if the engine mounted Glide while this card was display:none the
        // slides may measure 0-width — replace the node and remount
        const slide = glide.querySelector('.glide__slide');
        const track = glide.querySelector('.glide__track');
        if (builder.style.display !== 'none' && track && slide &&
            track.offsetWidth > 0 && slide.offsetWidth === 0) {
          const clone = glide.cloneNode(true);
          glide.parentNode.replaceChild(clone, glide);
          try { new Glide(clone).mount(); } catch (err) { /* noop */ }
        }
      });
    },

    /* ── option description "i" popovers ─────────────────────────── */
    _toggleInfo: function (btn) {
      const pop = btn.parentNode ? btn.parentNode.querySelector('.v3-infoPop') : null;
      if (!pop) return;
      if (!pop.hidden) { this._closeInfo(); return; }
      this._closeInfo();
      pop.hidden = false;
      const rect = btn.getBoundingClientRect();
      const pw = pop.offsetWidth;
      const ph = pop.offsetHeight;
      let left = Math.min(rect.left, window.innerWidth - pw - 8);
      if (left < 8) left = 8;
      let top = rect.bottom + 10;
      if (top + ph > window.innerHeight - 8) top = rect.top - ph - 10;
      pop.style.left = left + 'px';
      pop.style.top = top + 'px';
      btn.setAttribute('aria-expanded', 'true');
    },

    _closeInfo: function () {
      document.querySelectorAll('.v3-infoPop').forEach((pop) => {
        if (!pop.hidden) pop.hidden = true;
      });
      document.querySelectorAll('.v3-info[aria-expanded="true"]').forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
      });
    },

    /* ── fixed top bar visibility + auto-scroll on load ──────────── */
    _topbarState: function () {
      const shown = window.scrollY > TOP_BAR_H;
      if (shown !== this.topShown) {
        this.topShown = shown;
        document.body.classList.toggle('v3-topShown', shown);
      }
    },

    _initAutoScroll: function () {
      const self = this;
      let navType = '';
      try {
        const nav = performance.getEntriesByType('navigation');
        navType = nav.length && nav[0].type ? nav[0].type : '';
      } catch (err) { navType = ''; }

      if (location.hash) return;
      if (navType === 'back_forward') return;
      if (window.scrollY > TOP_BAR_H) return;

      window.addEventListener('pageshow', function (e) {
        if (e.persisted) self.autoScrolled = true;
      });

      const run = function () {
        if (self.autoScrolled) return;
        const h1 = document.querySelector('.prodTitle h1, h1.product-title');
        const main = document.querySelector('main');
        if (!h1 || !main) return;
        self.autoScrolled = true;

        // settle layout: wait until two consecutive frames agree
        let stable = 0;
        const settle = function () {
          const mainTop = main.offsetTop;
          const h1DocTop = h1.getBoundingClientRect().top + window.scrollY;
          const target = MOBILE.matches ? mainTop : Math.max(mainTop, h1DocTop - 16);
          stable++;
          if (stable < 3) { requestAnimationFrame(settle); return; }
          const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          const y = Math.min(target, maxY);
          self._animateScroll(y);
        };
        settle();
      };

      // wait for the full page (incl. header/offer images) so the
      // measured scroll target does not shift under us
      if (document.readyState === 'complete') run();
      else window.addEventListener('load', run);
    },

    _animateScroll: function (targetY) {
      const self = this;
      const startY = window.scrollY;
      const distance = targetY - startY;
      if (Math.abs(distance) < 2) return;
      if (REDUCED.matches) { window.scrollTo(0, targetY); return; }

      // global html{scroll-behavior:smooth} would animate every scrollTo
      // frame — temporarily disable it for our own eased animation
      document.documentElement.style.scrollBehavior = 'auto';
      const dur = Math.min(700, Math.max(350, Math.abs(distance) * 0.45));
      let cancelled = false;
      const cancel = function () { cancelled = true; };
      window.addEventListener('wheel', cancel, { passive: true, once: true });
      window.addEventListener('touchstart', cancel, { passive: true, once: true });
      window.addEventListener('keydown', cancel, { once: true });

      const t0 = performance.now();
      const step = function (now) {
        if (cancelled) { document.documentElement.style.scrollBehavior = ''; return; }
        const p = Math.min(1, (now - t0) / dur);
        const ease = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
        window.scrollTo(0, startY + distance * ease);
        if (p < 1) requestAnimationFrame(step);
        else document.documentElement.style.scrollBehavior = '';
      };
      requestAnimationFrame(step);
    }
  };

  document.addEventListener('DOMContentLoaded', function () {
    V3Wizard.init();
  });

  window.V3Wizard = V3Wizard;
})();
